/**
 * Who may change a person's job title (t128, t441u). One rule for the API, the web drawer, the phone web and the app:
 * - anyone who manages members (admin.users.manage: Owners and Admins) may change anyone's, their own included;
 * - anyone who may edit the person's employment (workforce.employees.edit) where the person works or in one of their
 *   teams may change theirs: the Workforce Admin everywhere, a Workforce Manager at their sites. That includes their own
 *   title when they work at one of those sites, as for any other person there.
 * There is no "set your own title" workspace setting: everyone else sees the title as plain text.
 */
import { canWith, hasPermission, type EffectiveGrant, type MyPermissions } from './permissions';

export const JOB_TITLE_ADMIN_PERMISSION = 'admin.users.manage';
export const JOB_TITLE_MANAGER_PERMISSION = 'workforce.employees.edit';

/** A permission check: does the viewer hold `perm` on a resource with these scope refs? */
export type JobTitlePermissionCheck = (perm: string, refs: readonly string[]) => boolean;

/** personRefs: the person's scope refs (`location:<id>` for the sites they work at, `team:<id>` for their teams). */
export function canEditJobTitle(can: JobTitlePermissionCheck, personRefs: readonly string[]): boolean {
  return can(JOB_TITLE_ADMIN_PERMISSION, []) || can(JOB_TITLE_MANAGER_PERMISSION, personRefs);
}

/** Server side: from the viewer's effective grants. */
export const canEditJobTitleByGrants = (grants: readonly EffectiveGrant[], personRefs: readonly string[]) =>
  canEditJobTitle((p, r) => hasPermission(grants, p, r), personRefs);

/** Client side: from GET /me/permissions. */
export const canEditJobTitleWith = (mine: Pick<MyPermissions, 'workspace' | 'scoped'> | null | undefined, personRefs: readonly string[]) =>
  canEditJobTitle((p, r) => canWith(mine, p, r), personRefs);

/** The person's scope refs as the API derives them (collab.resource_refs for a user), from their profile. */
export const personScopeRefs = (p: { buildings?: readonly { id: string }[] | null; teams?: readonly { id: string }[] | null }): string[] =>
  [...(p.buildings ?? []).map(b => `location:${b.id}`), ...(p.teams ?? []).map(t => `team:${t.id}`)];

/**
 * What the apps show: the API's answer on the person's profile (can.edit_job_title, worked out with canEditJobTitle),
 * or the same rule from the viewer's permissions when an older API leaves it out.
 */
export function jobTitleEditable(
  p: { can?: { edit_job_title?: boolean } | null; buildings?: readonly { id: string }[] | null; teams?: readonly { id: string }[] | null } | null | undefined,
  mine: Pick<MyPermissions, 'workspace' | 'scoped'> | null | undefined,
): boolean {
  if (!p) return false;
  if (typeof p.can?.edit_job_title === 'boolean') return p.can.edit_job_title;
  return canEditJobTitleWith(mine, personScopeRefs(p));
}
